import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { createStructuredSelector } from 'reselect';

// i18n and selectors
import { isLoggedInSelector, userSelector } from 'common/globalComponents/selectors';

// Menu components
import SchoolMyKidsMenuBar from './SchoolMyKidsMenuBar';
import TheParentZMenuBar from './TheParentZMenuBar';
import MediwatcherMenuBar from './MediwatcherMenuBar';
import { API_BASE_URL, isProduction } from 'utils/constants';
import AuthService from 'common/auth.service';
import RHAMenuBar from './RHAMenuBar';
import PeriodTrackerMenuBar from './PeriodTrackerMenuBar';

// Redux state
const stateSelector = createStructuredSelector({
  isLoggedIn: isLoggedInSelector,
  user: userSelector,
});

// Function to dynamically load the logo based on app source and type
const getLogo = async (type, appSource) => {
  try {
    const logoPath = `../../assets/images/logo/${appSource}/${type}.png`;
    const importedLogo = await import(`../../assets/images/logo/${appSource}/${type}.png`);
    return importedLogo.default || logoPath;
  } catch (error) {
    return '../../assets/images/logo/schoolmykids/logo.png';
  }
};

// Component to render the logo
export const RenderLogo = ({ leftMenu, appSource, currentAppSource }) => {
  const [logoSrc, setLogoSrc] = useState('');

  useEffect(() => {
    const loadLogo = async () => {
      const type = leftMenu ? 'small-logo' : 'logo';
      const src = await getLogo(type, appSource || 'schoolmykids');
      setLogoSrc(src);
    };

    loadLogo();
  }, [leftMenu, appSource]);

  if (currentAppSource === 'hr') {
    return (
      <img
        src={'https://www.rhatechnologies.com/static/images/rha-technologies-logo.svg'}
        height="35"
        alt={`RHA Technologies logo`}
      />
    );
  }

  return <img src={logoSrc} height="35" alt={`${appSource || 'schoolmykids'} logo`} />;
};

RenderLogo.propTypes = {
  leftMenu: PropTypes.bool.isRequired,
  appSource: PropTypes.string,
};

// Main Sidebar component
const Sidebar = (props) => {
  const auth = new AuthService();
  const currentAppSource = auth.getAppSourceCookie();

  const { user, isLoggedIn } = useSelector(stateSelector);
  const appSource = user?.appSource || 'schoolmykids';

  return (
    <div className="vertical-menu">
      <div className={isProduction ? 'bg-warning navbar-brand-box px-0' : 'navbar-brand-box px-0'}>
        <Link to="/" className="logo logo-dark">
          <span className="logo-sm">
            <RenderLogo leftMenu={true} appSource={appSource} currentAppSource={currentAppSource} />
          </span>
          <span className="logo-lg">
            <RenderLogo leftMenu={false} appSource={appSource} currentAppSource={currentAppSource} />
          </span>
        </Link>

        <Link to="/" className="logo logo-light">
          <span className="logo-sm">
            <RenderLogo leftMenu={true} appSource={appSource} currentAppSource={currentAppSource} />
          </span>
          <span className="logo-lg">
            <RenderLogo leftMenu={false} appSource={appSource} currentAppSource={currentAppSource} />
          </span>
        </Link>
      </div>
      <div data-simplebar className="h-100">
        {currentAppSource === 'hr' ? (
          <RHAMenuBar />
        ) : (
          <>
            {appSource === 'schoolmykids' && <SchoolMyKidsMenuBar />}
            {['theparentz', 'theparentz_com', 'theparentz_ai'].includes(appSource) && <TheParentZMenuBar />}
            {appSource === 'periodtracker' && <PeriodTrackerMenuBar />}
            {appSource === 'mediwatcher' && <MediwatcherMenuBar />}
          </>
        )}
      </div>
    </div>
  );
};

Sidebar.propTypes = {
  leftMenu: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  layout: state.Layout,
});

export default connect(mapStateToProps, {})(withRouter(withTranslation()(Sidebar)));
